import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { fileUploadService } from '../../services';
import { API_CHATBOT_BASE_URL } from '../../constants/config';

const DocumentUpload = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            // Fetch from reference API (RAG system)
            const response = await api.get(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents`);
            const docs = response.data.data || response.data || [];
            // Ensure docs is an array
            setDocuments(Array.isArray(docs) ? docs : []);
        } catch (err) {
            console.error('Failed to fetch documents', err);
            // Don't show critical error on initial load if just empty or not set up
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a file');
            return;
        }

        setUploading(true);
        setError('');
        setSuccess('');

        try {
            // 1. Upload file using existing service (optional, if we want to store it in our bucket first)
            // Or directly to Reference API. Reference API likely needs the file.
            // Let's assume we send directly to Reference API as "rag should read from this docs".

            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', title);
            formData.append('description', description);

            await api.post(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setSuccess('Document uploaded successfully for RAG processing!');
            setTitle('');
            setDescription('');
            setFile(null);

            // Refresh list
            fetchDocuments();

        } catch (err) {
            console.error('Upload failed', err);
            const msg = err.response?.data?.message || err.response?.data?.detail || err.message || 'Unknown error';
            setError(`Failed to upload document. ${msg}`);
            // Check for auth error
            if (err.response?.status === 401) {
                setError('Authentication failed. Please try logging in again.');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            await api.delete(`${API_CHATBOT_BASE_URL}api/v1/admin/rag/documents/${id}`);
            setDocuments(documents.filter(d => d.id !== id));
            setSuccess('Document deleted');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete document');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4">
                <div className="mb-8">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">RAG Document Upload</h1>
                    <p className="text-gray-600">Upload documents (PDF) for the AI to learn about party information.</p>
                </div>

                {error && <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg">{success}</div>}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Upload Form */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upload New Document</h2>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        required
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="e.g. Party Constitution 2024"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border rounded-md"
                                        placeholder="What is this document about?"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">File</label>
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        required
                                        accept=".pdf,.doc,.docx,.txt"
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Supported: PDF</p>
                                </div>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {uploading ? 'Uploading...' : 'Upload & Process'}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* Document List */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-md overflow-hidden">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-800">Uploaded Documents</h2>
                            </div>
                            {loading ? (
                                <div className="p-8 text-center text-gray-500">Loading documents...</div>
                            ) : documents.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No documents uploaded yet.</div>
                            ) : (
                                <ul className="divide-y divide-gray-200">
                                    {documents.map(doc => (
                                        <li key={doc.id} className="p-6 hover:bg-gray-50 flex justify-between items-start">
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900">{doc.title}</h3>
                                                {doc.description && <p className="text-gray-600 mt-1">{doc.description}</p>}
                                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                                    <span>{doc.filename}</span>
                                                    <span>•</span>
                                                    <span>{new Date(doc.uploaded_at || doc.created_at).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDelete(doc.id)}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                            >
                                                Delete
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentUpload;
